import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Building2, ArrowLeft, CheckCircle, XCircle, KeyRound } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link, useSearch } from "wouter";
import { PortalPage, PortalHero, InlineError, GoldButton } from "./portal/shared";

const resetPasswordSchema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  usePageTitle("Reset Password");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get("token");
  const context = params.get("context");
  const isPortal = context === "portal";
  const loginHref = isPortal ? "/portal/login" : "/login";
  const forgotHref = isPortal ? "/forgot-password?context=portal" : "/forgot-password";
  
  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { data: tokenValidation, isLoading: isValidating } = useQuery({
    queryKey: ["/api/auth/verify-reset-token", token],
    queryFn: async () => {
      const response = await fetch(`/api/auth/verify-reset-token?token=${token}`);
      return response.json();
    },
    enabled: !!token,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordForm) => {
      const response = await apiRequest("POST", "/api/auth/reset-password", {
        token,
        newPassword: data.newPassword,
      });
      return response;
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to reset password. Please try again.");
    },
  });

  const handleSubmit = (data: ResetPasswordForm) => {
    setError(null);
    resetPasswordMutation.mutate(data);
  };

  if (isPortal) {
    const portalCard = (content: React.ReactNode) => (
      <PortalPage>
        <PortalHero
          eyebrow="Don Norman Associates"
          title={<>Service Bureau<br />Portal</>}
          subtitle="Secure access for authorized Service Bureau clients."
        />
        <div style={{ padding: "0 24px 64px", maxWidth: 440, margin: "-48px auto 0", position: "relative" }}>
          <div style={{ background: "white", borderRadius: 16, boxShadow: "0 20px 60px rgba(27,43,75,0.18)", padding: "40px 48px" }}>
            {content}
          </div>
        </div>
      </PortalPage>
    );

    if (!token) {
      return portalCard(
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 56, height: 56, background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <XCircle size={28} color="#dc2626" />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 8 }}>
            Invalid Link
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
            This password reset link is invalid. Please request a new one.
          </p>
          <GoldButton onClick={() => { window.location.href = forgotHref; }} data-testid="link-request-new">
            Request New Reset Link
          </GoldButton>
        </div>
      );
    }

    if (isValidating) {
      return portalCard(
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ width: 40, height: 40, border: "3px solid #e2ddd6", borderTopColor: "#1B2B4B", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ fontSize: 14, color: "#6b7280" }}>Validating reset link…</p>
        </div>
      );
    }

    if (tokenValidation && !tokenValidation.valid) {
      return portalCard(
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 56, height: 56, background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <XCircle size={28} color="#dc2626" />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 8 }}>
            Link Expired
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
            This password reset link has expired or is invalid. Please request a new one.
          </p>
          <GoldButton onClick={() => { window.location.href = forgotHref; }} data-testid="link-request-new">
            Request New Reset Link
          </GoldButton>
        </div>
      );
    }

    if (success) {
      return portalCard(
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 56, height: 56, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <CheckCircle size={28} color="#16a34a" />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 8 }}>
            Password Reset!
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 24 }}>
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <GoldButton onClick={() => { window.location.href = loginHref; }} data-testid="link-go-to-login">
            Go to Login
          </GoldButton>
        </div>
      );
    }

    return portalCard(
      <>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 48, height: 48, background: "#C9962A", borderRadius: 10, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 900, fontSize: 18, color: "white" }}>
            DNA
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: "#1B2B4B", marginBottom: 4 }}>
            Reset Your Password
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280" }}>
            Enter your new password below
          </p>
        </div>

        {error && <InlineError message={error} />}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#1B2B4B", display: "block", marginBottom: 6 }}>
                    New Password <span style={{ color: "#C9962A" }}>*</span>
                  </FormLabel>
                  <FormControl>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      data-testid="input-new-password"
                      {...field}
                      style={{ width: "100%", padding: "13px 16px", fontSize: 15, color: "#1a1a1a", background: "white", border: "1.5px solid #e2ddd6", borderRadius: 10, outline: "none", boxSizing: "border-box" }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#1B2B4B", display: "block", marginBottom: 6 }}>
                    Confirm Password <span style={{ color: "#C9962A" }}>*</span>
                  </FormLabel>
                  <FormControl>
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      data-testid="input-confirm-password"
                      {...field}
                      style={{ width: "100%", padding: "13px 16px", fontSize: 15, color: "#1a1a1a", background: "white", border: "1.5px solid #e2ddd6", borderRadius: 10, outline: "none", boxSizing: "border-box" }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <GoldButton
              type="submit"
              disabled={resetPasswordMutation.isPending}
              data-testid="button-reset-password"
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <Loader2 size={18} style={{ animation: "spin 0.8s linear infinite" }} />
                  Resetting…
                </>
              ) : (
                "Reset Password"
              )}
            </GoldButton>
          </form>
        </Form>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <a
            href={loginHref}
            data-testid="link-back-to-login"
            style={{ fontSize: 13, color: "#C9962A", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
            onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            <ArrowLeft size={13} />
            Back to Login
          </a>
        </div>
      </>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Invalid Link</CardTitle>
            <CardDescription className="text-center">
              This password reset link is invalid. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={forgotHref}>
              <Button className="w-full" data-testid="link-request-new">
                Request New Reset Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Validating reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValidation && !tokenValidation.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Link Expired</CardTitle>
            <CardDescription className="text-center">
              This password reset link has expired or is invalid. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={forgotHref}>
              <Button className="w-full" data-testid="link-request-new">
                Request New Reset Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Password Reset!</CardTitle>
            <CardDescription className="text-center">
              Your password has been successfully reset. You can now log in with your new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={loginHref}>
              <Button className="w-full" data-testid="link-go-to-login">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <KeyRound className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Reset Your Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Enter new password"
                        data-testid="input-new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        data-testid="input-confirm-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button
                type="submit"
                className="w-full"
                disabled={resetPasswordMutation.isPending}
                data-testid="button-reset-password"
              >
                {resetPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </Form>
          
          <div className="text-center">
            <Link href={loginHref} className="text-sm text-primary hover:underline" data-testid="link-back-to-login">
              <ArrowLeft className="h-4 w-4 inline-block mr-1" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
